/**
 * AuthorDetailView — Full author dossier. Renders the entire schema in a
 * single scrollable page: header (identity card) + narrative left column +
 * works/themes/relations right column. Every "grabbable" block carries
 * `data-fragment-id` for the future lesson-builder.
 *
 * Layout: single-column by default. `views.css` lifts the inner grid to
 * 2-column on >=1400px (narrative left, aside right, sticky).
 *
 * LIM mode (task 14): when `document.documentElement.dataset.display === 'lim'`
 * we hand off rendering to SlidePresenter instead — see onInit() guard.
 */

import { BaseComponent } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type {
  Author,
  Work,
  MajorWork,
  Passage,
  ThemeTreatment,
  AuthorRelations,
} from '@/data/schema';
import { fragmentId, paragraphIndex } from '@/types/fragments';
import {
  periodLabel,
  movementLabel,
  genreLabel,
  themeLabel,
  themeKind,
} from '@/data/vocab';
import type { UiLocale } from '@/data/vocab';
import { SlidePresenter, type Slide } from '@/components/SlidePresenter';
import { mountAuthorMap, type AuthorMapHandle } from '@/components/AuthorMapView';
import { escapeHtml, escapeAttr } from '@/core/escape';

const RELATION_KEYS: Array<keyof AuthorRelations> = [
  'influenced_by',
  'influenced',
  'contemporary_with',
  'responds_to',
  'adapts',
];

const MAJOR_WORK_SECTIONS: Array<{ key: keyof MajorWork; i18n: string }> = [
  { key: 'summary',              i18n: 'work.summary' },
  { key: 'structure',            i18n: 'work.structure' },
  { key: 'characters',           i18n: 'work.characters' },
  { key: 'themes',               i18n: 'work.themes' },
  { key: 'form_and_style',       i18n: 'work.form_and_style' },
  { key: 'reception_and_legacy', i18n: 'work.reception_and_legacy' },
];

export interface AuthorDetailOptions {
  authorId: string;
  /** Deep-link dalla Mappa dei temi: all'atterraggio scrolla al trattamento. */
  scrollToThemeId?: string;
  /** Etichetta del "← Indietro" = rotta d'origine (Mappa dei temi / Rete /
   *  Autori). Se assente ricade su "Autori". */
  backLabel?: string;
}

export class AuthorDetailView extends BaseComponent {
  private readonly authorId: string;
  private readonly backLabel: string | null;
  private pendingScrollTheme: string | null = null;
  private slidePresenter: SlidePresenter | null = null;
  private mapHandle: AuthorMapHandle | null = null;
  private displayObserver: MutationObserver | null = null;

  constructor(
    element: HTMLElement,
    context: ConstructorParameters<typeof BaseComponent>[1],
    options: AuthorDetailOptions,
  ) {
    super(element, context);
    this.authorId = options.authorId;
    this.backLabel = options.backLabel ?? null;
    this.pendingScrollTheme = options.scrollToThemeId ?? null;
  }

  protected async onInit(): Promise<void> {
    // Single delegated click handler — attached ONCE for the lifetime of the
    // component so re-renders (locale change, LIM toggle) don't pile up
    // listeners that would multiply event firing.
    this.addEventListener(this.element, 'click', (ev) => this.handleClick(ev));

    this.render();

    // Auto re-render when LIM mode toggles via document.documentElement.dataset.display
    this.displayObserver = new MutationObserver(() => this.render());
    this.displayObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-display'],
    });
    this.addCleanup(() => this.displayObserver?.disconnect());

    // La mappa ha layout diversi in orizzontale/verticale (telefono ruotato):
    // al flip di orientamento ri-renderizziamo la vista mappa.
    let lastPortrait = window.innerHeight > window.innerWidth;
    const onResize = () => {
      const p = window.innerHeight > window.innerWidth;
      if (p !== lastPortrait) {
        lastPortrait = p;
        if (isLimMode()) this.render();
      }
    };
    window.addEventListener('resize', onResize);
    this.addCleanup(() => window.removeEventListener('resize', onResize));
  }

  private handleClick(ev: Event): void {
    const t = ev.target as HTMLElement;

    // Back button (normal mode) → torna alla rotta d'origine (gestita dal router)
    if (t.closest('.ad-back')) {
      this.context.bus.emit('navigate:back', {});
      return;
    }

    // ✕ in vista Mappa = CHIUDI APPROFONDIMENTO (torna alla mappa d'insieme).
    // 🐞 Prima faceva `delete dataset.display` = usciva in Testo SENZA avvisare
    // il SystemTray → toggle Mappa/Testo disallineato (serviva un click a
    // vuoto per riallinearlo). La modalità si cambia SOLO dal toggle del tray.
    if (t.closest('[data-lim-action="exit"]')) {
      this.mapHandle?.reset();
      return;
    }

    // LIM mode switch (Map / Slides)
    const modeBtn = t.closest<HTMLElement>('[data-lim-mode]');
    if (modeBtn?.dataset.limMode) {
      const newMode = modeBtn.dataset.limMode as 'map' | 'slides';
      this.element.querySelectorAll<HTMLElement>('[data-lim-mode]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.limMode === newMode);
      });
      this.switchLimMode(newMode);
      return;
    }

    // Chip-ancora (Ode, Epica, Romanticismo…) → scroll al trattamento del tema
    const themeChip = t.closest<HTMLElement>('[data-scroll-theme]');
    if (themeChip) {
      const themeId = themeChip.getAttribute('data-scroll-theme');
      if (themeId) this.scrollToTheme(themeId);
      return;
    }

    // Link Tema→Opera (#work-id): il salto hash nativo NON apre i <details>
    // → apriamo la tendina Opere + l'opera stessa, poi scroll morbido.
    const workLink = t.closest<HTMLAnchorElement>('a[href^="#work-"]');
    if (workLink) {
      ev.preventDefault();
      const id = workLink.getAttribute('href')!.slice(1);
      const li = this.element.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
      if (li) {
        const fold = li.closest<HTMLDetailsElement>('details.ad-section-fold');
        if (fold) fold.open = true;
        const inner = li.querySelector<HTMLDetailsElement>('details');
        if (inner) inner.open = true;
        li.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    // Click on a related-author chip → navigate to that author
    const authorLink = t.closest<HTMLElement>('[data-author-link]');
    if (authorLink) {
      const id = authorLink.getAttribute('data-author-link');
      if (id) this.context.bus.emit('navigate:author', { id });
    }
  }

  /** Scrolla al trattamento del tema (aprendo la tendina Temi se chiusa)
   *  con flash di evidenziazione. Usato da chip-ancora e deep-link. */
  private scrollToTheme(themeId: string): void {
    const target = this.element.querySelector<HTMLElement>(
      `.ad-theme[data-theme-id="${CSS.escape(themeId)}"]`,
    );
    if (!target) return;
    const fold = target.closest<HTMLDetailsElement>('details.ad-section-fold');
    if (fold) fold.open = true;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.classList.remove('is-anchored');
    void target.offsetWidth; // restart animation
    target.classList.add('is-anchored');
  }

  /** Switch between map and slides INSIDE the current LIM session. */
  private switchLimMode(mode: 'map' | 'slides'): void {
    const author = getDataService().getAuthor(this.authorId);
    if (!author) return;
    const stage = this.element.querySelector<HTMLElement>('[data-lim-stage]');
    if (!stage) return;
    this.mountLimMode(mode, author, this.context.locale as UiLocale, stage);
  }

  protected onLocaleChange(): void {
    this.render();
  }

  protected onDispose(): void {
    this.teardownLim();
  }

  private teardownLim(): void {
    if (this.slidePresenter) {
      this.slidePresenter.dispose();
      this.slidePresenter = null;
    }
    if (this.mapHandle) {
      this.mapHandle.dispose();
      this.mapHandle = null;
    }
  }

  private render(): void {
    this.teardownLim();

    const author = getDataService().getAuthor(this.authorId);
    if (!author) {
      this.element.classList.add('author-detail-view', 'author-detail-view--missing');
      this.element.innerHTML = `<p class="ad-missing">${this.t('error.notFound')}</p>`;
      return;
    }

    if (isLimMode()) {
      this.renderLim(author);
      return;
    }

    this.renderNormal(author);
  }

  private renderNormal(author: Author): void {
    const locale = this.context.locale as UiLocale;
    const fid = fragmentId(author.id);

    this.element.classList.add('author-detail-view');
    // Tornando da Mappa→Testo la classe --lim va TOLTA, altrimenti la vista
    // resta absolute/full-bleed e il testo perde i margini laterali.
    this.element.classList.remove('author-detail-view--lim', 'author-detail-view--missing');
    this.element.setAttribute('data-author-id', author.id);
    this.element.setAttribute('data-fragment-id', fid);

    this.element.innerHTML = `
      ${this.renderHeader(author, locale)}
      ${this.renderIntro(author, locale)}
      <div class="ad-grid">
        ${this.renderNarrative(author)}
        ${this.renderAside(author, locale)}
      </div>
    `;
    // All click handling is done by the single delegated listener attached in onInit().

    // Deep-link dalla Mappa dei temi: atterra direttamente sul trattamento.
    if (this.pendingScrollTheme) {
      const themeId = this.pendingScrollTheme;
      this.pendingScrollTheme = null; // una sola volta (non a ogni re-render)
      requestAnimationFrame(() => this.scrollToTheme(themeId));
    }
  }

  /** Tagline + chips block — sits below the fixed header, scrolls with content. */
  private renderIntro(a: Author, locale: UiLocale): string {
    // SOLO i chip con un trattamento tema presente in pagina (es. Ode, Epica,
    // Romanticismo): sono ancore → scroll alla sezione. Quelli senza una
    // sezione dedicata non danno alcuna azione → non si mostrano (richiesta
    // utente 2026-06-10: "solo i tasti che hanno senso per ogni autore").
    const treated = new Set<string>(a.theme_treatments.map(t => t.theme_id));
    const periodChips = a.periods.filter(p => treated.has(p)).map(p => chip('period', periodLabel(p, locale), p)).join('');
    const movementChips = a.movements.filter(m => treated.has(m)).map(m => chip('movement', movementLabel(m, locale), m)).join('');
    const genreChips = a.genres.filter(g => treated.has(g)).map(g => chip('genre', genreLabel(g, locale), g)).join('');
    return `
      <section class="ad-intro">
        ${a.tagline
          ? `<p class="ad-tagline" data-fragment-id="${escapeAttr(fragmentId(a.id, 'tagline'))}">${escapeHtml(a.tagline)}</p>`
          : ''}
        <div class="ad-chips">${periodChips}${movementChips}${genreChips}</div>
      </section>
    `;
  }

  private renderLim(author: Author): void {
    const locale = this.context.locale as UiLocale;
    this.element.classList.add('author-detail-view', 'author-detail-view--lim');
    this.element.classList.remove('author-detail-view--missing');
    // In vista MAPPA la radice NON è un frammento catturabile: l'attributo
    // (ereditato dalla vista Testo) attivava la regola CSS del lesson-builder
    // `[data-fragment-id]{position:relative}` che ROMPEVA l'absolute della
    // vista → mappa spinta fuori schermo a destra (solo con cattura attiva).
    this.element.removeAttribute('data-fragment-id');
    this.element.innerHTML = `
      <div class="lim-toolbar">
        <button type="button" class="lim-btn" data-lim-action="exit" title="${this.t('lim.exit')}">✕</button>
        <div class="lim-mode-switch" role="tablist">
          <button type="button" class="lim-mode-btn is-active" data-lim-mode="map">${this.t('lim.modeMap')}</button>
          <button type="button" class="lim-mode-btn" data-lim-mode="slides">${this.t('lim.modeSlides')}</button>
        </div>
      </div>
      <div class="lim-stage" data-lim-stage></div>
    `;

    const stage = this.element.querySelector<HTMLElement>('[data-lim-stage]')!;
    this.mountLimMode('map', author, locale, stage);
    // Clicks (exit, mode switch) are handled by the single delegated listener in onInit().
  }

  private mountLimMode(mode: 'map' | 'slides', author: Author, locale: UiLocale, stage: HTMLElement): void {
    if (this.slidePresenter) { this.slidePresenter.dispose(); this.slidePresenter = null; }
    if (this.mapHandle) { this.mapHandle.dispose(); this.mapHandle = null; }
    stage.innerHTML = '';

    // ✕ = "chiudi approfondimento": visibile SOLO durante il drill-in mappa.
    const exitBtn = this.element.querySelector<HTMLElement>('[data-lim-action="exit"]');
    exitBtn?.classList.remove('is-visible');

    if (mode === 'map') {
      mountAuthorMap(stage, author.id, locale, (action) => {
        if (action.kind === 'author' && action.id) {
          this.context.bus.emit('navigate:author', { id: action.id });
        }
      }).then(handle => {
        this.mapHandle = handle;
        handle.onDrillChange((drilled) => exitBtn?.classList.toggle('is-visible', drilled));
      });
      return;
    }
    // 'slides' fallback
    const slides = this.buildSlides(author, locale);
    this.slidePresenter = new SlidePresenter(stage, slides, {
      // Uscire dalle slide RESTA in modalità Mappa (si torna alla mappa
      // d'insieme): cambiare display qui disallineava il toggle del tray.
      onExit: () => {
        this.element.querySelectorAll<HTMLElement>('[data-lim-mode]').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.limMode === 'map');
        });
        this.switchLimMode('map');
      },
      backLabel: this.t('sidebar.back'),
    });
    this.slidePresenter.mount();
  }

  private buildSlides(author: Author, locale: UiLocale): Slide[] {
    const slides: Slide[] = [];
    const name = author.name;

    // Title slide
    slides.push({
      sectionTitle: this.t('app.name'),
      html: `
        <div>
          <p class="sp-title">${escapeHtml(name)}</p>
          ${author.birth_year ? `<p class="sp-author">${author.birth_year}${author.death_year ? '–' + author.death_year : ''} · ${escapeHtml(author.nationality)}</p>` : ''}
          ${author.tagline ? `<p class="sp-statement">${escapeHtml(author.tagline)}</p>` : ''}
        </div>
      `,
    });

    // UNA SLIDE PER PARAGRAFO (criteri v2, doc 04): il paragrafo è l'unità
    // naturale — un campo intero in una slide trabocca e va coperto in alto.
    // Fallback VISIVO (doc 04 §3): paragrafi oltre ~100 parole spezzati sul
    // confine di frase. Con più slide il sottotitolo è numerato "Biografia (2/3)".
    const wordsOf = (html: string) =>
      html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
    const splitLong = (p: string, maxWords = 85): string[] => {
      if (wordsOf(p) <= maxWords + 15) return [p];
      const sentences = p.split(/(?<=[.!?…])\s+(?=["'<A-Z])/);
      const chunks: string[] = [];
      let cur = '';
      let curWords = 0;
      for (const s of sentences) {
        const w = wordsOf(s);
        if (curWords > 0 && curWords + w > maxWords) {
          chunks.push(cur);
          cur = s;
          curWords = w;
        } else {
          cur = cur ? `${cur} ${s}` : s;
          curWords += w;
        }
      }
      if (cur) chunks.push(cur);
      return chunks;
    };
    const pushField = (subtitle: string, html?: string | null) => {
      if (!html?.trim()) return;
      const paras = splitParagraphs(html).flatMap(p => splitLong(p));
      paras.forEach((p, i) => {
        slides.push({
          sectionTitle: name,
          subtitle: paras.length > 1 ? `${subtitle} (${i + 1}/${paras.length})` : subtitle,
          html: `<p>${p}</p>`,
        });
      });
    };

    // Narrative sections
    pushField(this.t('section.biography'), author.biography);
    pushField(this.t('section.historical_context'), author.historical_context);
    pushField(this.t('section.style_and_themes'), author.style_and_themes);
    pushField(this.t('section.significance'), author.significance);

    // Major works (summary)
    for (const w of author.works) {
      if (!w.is_major) continue;
      pushField(`${w.title}${w.year ? ` (${w.year})` : ''}`, w.summary);
    }

    // Theme treatments
    for (const t of author.theme_treatments) {
      pushField(themeLabel(t.theme_id, locale), t.explanation);
    }

    return slides;
  }

  // ─────────────── HEADER ───────────────

  private renderHeader(a: Author, locale: UiLocale): string {
    // Compact fixed top bar: just back + portrait + name + dates on one line.
    // Tagline + chips moved to .ad-intro section below (scrolls with content).
    void locale;
    const dates = formatDates(a.birth_year, a.death_year);
    return `
      <header class="ad-header">
        <button type="button" class="ad-back" aria-label="${this.t('sidebar.back')}"><span aria-hidden="true">←</span> <span class="ad-back-label">${escapeHtml(this.backLabel ?? this.t('nav.authors'))}</span></button>
        <div class="ad-header-main">
          <div class="ad-portrait" aria-hidden="true">${escapeHtml(initials(a.name))}</div>
          <div class="ad-identity">
            <h1 class="ad-name">${escapeHtml(a.name)}</h1>
            ${dates
              ? `<p class="ad-dates"><span class="ad-dates-main">${escapeHtml(dates)}</span><span class="ad-dates-extra"> · ${escapeHtml(a.nationality)}${a.place ? ' · ' + escapeHtml(a.place) : ''}</span></p>`
              : ''}
          </div>
        </div>
      </header>
    `;
  }

  // ─────────────── NARRATIVE (left column) ───────────────

  private renderNarrative(a: Author): string {
    return `
      <div class="ad-narrative">
        ${this.renderProseSection(a.id, 'biography',          'section.biography',          a.biography)}
        ${this.renderProseSection(a.id, 'historical_context', 'section.historical_context', a.historical_context)}
        ${this.renderProseSection(a.id, 'style_and_themes',   'section.style_and_themes',   a.style_and_themes)}
        ${this.renderProseSection(a.id, 'significance',       'section.significance',       a.significance)}
      </div>
    `;
  }

  private renderProseSection(authorId: string, sectionKey: string, i18nKey: string, html: string): string {
    if (!html?.trim()) return '';
    const paragraphs = splitParagraphs(html);
    const body = paragraphs
      .map((p, i) => `<p data-fragment-id="${escapeAttr(fragmentId(authorId, sectionKey, paragraphIndex(i)))}">${p}</p>`)
      .join('');
    // Ogni macro-sezione è una TENDINA (aperta di default, chiudibile):
    // la navigazione della scheda diventa molto più rapida. Vale su tutti i
    // dispositivi (scelta utente 2026-06-10).
    return `
      <details class="ad-section ad-section-fold" id="section-${escapeAttr(sectionKey)}" open>
        <summary class="ad-section-title" data-fragment-id="${escapeAttr(fragmentId(authorId, sectionKey, 'title'))}">${this.t(i18nKey)}</summary>
        <div class="ad-prose">${body}</div>
      </details>
    `;
  }

  // ─────────────── ASIDE (right column) ───────────────

  private renderAside(a: Author, locale: UiLocale): string {
    return `
      <aside class="ad-aside">
        ${this.renderWorks(a, locale)}
        ${this.renderThemeTreatments(a, locale)}
        ${this.renderRelations(a)}
      </aside>
    `;
  }

  // ─────────────── WORKS ───────────────

  private renderWorks(a: Author, locale: UiLocale): string {
    const major = a.works.filter((w): w is MajorWork => w.is_major);
    const minor = a.works.filter(w => !w.is_major);

    return `
      <details class="ad-section ad-section-works ad-section-fold" id="section-works" open>
        <summary class="ad-section-title">${this.t('section.works')}</summary>

        ${major.length > 0 ? `
          <h3 class="ad-subsection-title">${this.t('section.major_works')}</h3>
          <ul class="ad-works-major" role="list">
            ${major.map(w => this.renderMajorWork(a.id, w, locale)).join('')}
          </ul>
        ` : ''}

        ${minor.length > 0 ? `
          <h3 class="ad-subsection-title">${this.t('section.minor_works')}</h3>
          <ul class="ad-works-minor" role="list">
            ${minor.map(w => this.renderMinorWork(a.id, w, locale)).join('')}
          </ul>
        ` : ''}
      </details>
    `;
  }

  private renderMajorWork(authorId: string, w: MajorWork, locale: UiLocale): string {
    const fid = fragmentId(authorId, 'works', w.id);
    const sections = MAJOR_WORK_SECTIONS
      .map(meta => this.renderWorkSubsection(authorId, w, meta.key, meta.i18n))
      .filter(Boolean)
      .join('');
    const subWorks = w.sub_works && w.sub_works.length > 0 ? `
      <section class="ad-work-section" data-work-section="sub_works">
        <h4 class="ad-work-section-title">${this.t('work.sub_works')}</h4>
        <ul class="ad-sub-works">
          ${w.sub_works.map((sw, i) => `
            <li data-fragment-id="${escapeAttr(fragmentId(authorId, 'works', w.id, 'sub_works', paragraphIndex(i)))}">
              <strong>${escapeHtml(sw.title)}</strong> — ${escapeHtml(sw.one_line)}
            </li>
          `).join('')}
        </ul>
      </section>
    ` : '';
    const passages = w.passages && w.passages.length > 0
      ? this.renderNestedPassages(authorId, w.id, w.passages)
      : '';

    return `
      <li class="ad-work" id="work-${escapeAttr(w.id)}" data-work-id="${escapeAttr(w.id)}" data-fragment-id="${escapeAttr(fid)}">
        <details>
          <summary class="ad-work-summary">
            <span class="ad-work-title"><strong>${escapeHtml(w.title)}</strong>${w.year ? ` <span class="ad-work-year">(${w.year})</span>` : ''}</span>
            <span class="ad-work-genre">${escapeHtml(genreLabel(w.genre, locale))}</span>
          </summary>
          <div class="ad-work-body">
            <p class="ad-work-oneline" data-fragment-id="${escapeAttr(fragmentId(authorId, 'works', w.id, 'one_line'))}">${escapeHtml(w.one_line)}</p>
            ${sections}
            ${subWorks}
            ${passages}
          </div>
        </details>
      </li>
    `;
  }

  private renderWorkSubsection(
    authorId: string,
    w: MajorWork,
    key: keyof MajorWork,
    i18nKey: string,
  ): string {
    const value = w[key];
    if (value === null || value === undefined || typeof value !== 'string') return '';
    if (!value.trim()) return '';
    const paragraphs = splitParagraphs(value);
    const body = paragraphs
      .map((p, i) => `<p data-fragment-id="${escapeAttr(fragmentId(authorId, 'works', w.id, key, paragraphIndex(i)))}">${p}</p>`)
      .join('');
    return `
      <section class="ad-work-section" data-work-section="${escapeAttr(key)}">
        <h4 class="ad-work-section-title">${this.t(i18nKey)}</h4>
        <div class="ad-prose">${body}</div>
      </section>
    `;
  }

  private renderNestedPassages(authorId: string, workId: string, passages: Passage[]): string {
    return `
      <section class="ad-work-section" data-work-section="passages">
        <h4 class="ad-work-section-title">${this.t('section.passages')}</h4>
        <ul class="ad-passages" role="list">
          ${passages.map(p => this.renderPassage(authorId, workId, p)).join('')}
        </ul>
      </section>
    `;
  }

  /**
   * Un passo. In versi il testo va AFFIANCATO alla parafrasi — è il modo in cui
   * la poesia si legge in classe: originale a sinistra, resa moderna a destra,
   * metrica e figure sotto. In prosa resta la colonna unica del golden.
   *
   * L'affiancamento è puro CSS (`.ad-passage-cols`, vedi views.css): su
   * telefono e su LIM stretta le due colonne si impilano da sole, senza logica
   * di superficie qui dentro — regola 6 degli invarianti.
   */
  private renderPassage(authorId: string, workId: string, p: Passage): string {
    const fid = (part: string) =>
      escapeAttr(fragmentId(authorId, 'works', workId, 'passages', `${p.id}/${part}`));

    // L'etichetta a sinistra c'è solo quando c'è la parafrasi: serve a tenere le
    // due colonne sulla stessa riga di partenza, non a nominare l'ovvio.
    const etichettaTesto = p.parafrasi
      ? `<span class="ad-label">${this.t('passage.testo')}</span>`
      : '';
    const testo = `<blockquote class="ad-passage-text" data-fragment-id="${fid('testo')}">${etichettaTesto}${preserveLinebreaks(p.text)}</blockquote>`;

    const parafrasi = p.parafrasi
      ? `<div class="ad-passage-parafrasi" data-fragment-id="${fid('parafrasi')}">
           <span class="ad-label">${this.t('passage.parafrasi')}</span>
           ${preserveLinebreaks(p.parafrasi)}
         </div>`
      : '';

    const corpo = parafrasi
      ? `<div class="ad-passage-cols">${testo}${parafrasi}</div>`
      : testo;

    const metrica = p.metrica
      ? `<p class="ad-passage-metrica" data-fragment-id="${fid('metrica')}"><span class="ad-label">${this.t('passage.metrica')}:</span> ${escapeHtml(p.metrica)}</p>`
      : '';

    const figure = p.figure_retoriche?.length
      ? `<details class="ad-passage-figure" data-fragment-id="${fid('figure')}">
           <summary>${this.t('passage.figure')} <span class="ad-passage-figure-count">${p.figure_retoriche.length}</span></summary>
           <ul role="list">
             ${p.figure_retoriche.map(f => `
               <li>
                 <strong>${escapeHtml(f.tipo)}</strong>
                 <em>«${escapeHtml(f.occorrenza)}»</em>
                 <span>${escapeHtml(f.spiegazione)}</span>
               </li>
             `).join('')}
           </ul>
         </details>`
      : '';

    return `
      <li class="ad-passage${p.is_verse ? ' ad-passage--verse' : ''}" data-passage-id="${escapeAttr(p.id)}"
          data-fragment-id="${escapeAttr(fragmentId(authorId, 'works', workId, 'passages', p.id))}">
        ${corpo}
        ${metrica}
        ${figure}
        <p class="ad-passage-ref"><span class="ad-label">${this.t('passage.reference')}:</span> ${escapeHtml(p.reference)}</p>
        <p class="ad-passage-note"><span class="ad-label">${this.t('passage.note')}:</span> ${escapeHtml(p.note)}</p>
        <p class="ad-passage-license">${this.t(`passage.license.${p.license}`)}</p>
      </li>
    `;
  }

  /**
   * Opera minore: scheda breve, più i passi se ne ha.
   *
   * I passi stanno su `BaseWork`, non su `MajorWork` (ADR 0001): in italiano un
   * testo capitale può appartenere a un'opera minore — la lettera a Vettori, la
   * scena di fra Timoteo, la *Mirra*. Senza questo ramo resterebbero nei dati e
   * fuori dallo schermo.
   */
  private renderMinorWork(authorId: string, w: Work, locale: UiLocale): string {
    const fid = fragmentId(authorId, 'works', w.id, 'one_line');
    const passages = w.passages && w.passages.length > 0
      ? this.renderNestedPassages(authorId, w.id, w.passages)
      : '';
    return `
      <li class="ad-work-minor" data-work-id="${escapeAttr(w.id)}" data-fragment-id="${escapeAttr(fid)}">
        <strong>${escapeHtml(w.title)}</strong>${w.year ? ` <span class="ad-work-year">(${w.year})</span>` : ''}
        <span class="ad-work-genre"> · ${escapeHtml(genreLabel(w.genre, locale))}</span>
        <p class="ad-work-oneline">${escapeHtml(w.one_line)}</p>
        ${passages}
      </li>
    `;
  }

  // ─────────────── THEME TREATMENTS ───────────────

  private renderThemeTreatments(a: Author, locale: UiLocale): string {
    if (a.theme_treatments.length === 0) return '';
    return `
      <details class="ad-section ad-section-themes ad-section-fold" id="section-themes" open>
        <summary class="ad-section-title">${this.t('section.theme_treatments')}</summary>
        <ul class="ad-themes" role="list">
          ${a.theme_treatments.map(t => this.renderThemeTreatment(a, t, locale)).join('')}
        </ul>
      </details>
    `;
  }

  private renderThemeTreatment(a: Author, t: ThemeTreatment, locale: UiLocale): string {
    const fid = fragmentId(a.id, 'theme_treatments', t.theme_id);
    const kind = themeKind(t.theme_id);
    const label = themeLabel(t.theme_id, locale);
    const work = getDataService().getWork(a.id, t.key_work_id);
    const passage = getDataService().getPassage(a.id, t.passage_id);
    const paragraphs = splitParagraphs(t.explanation);
    const body = paragraphs
      .map((p, i) => `<p data-fragment-id="${escapeAttr(fragmentId(a.id, 'theme_treatments', t.theme_id, paragraphIndex(i)))}">${p}</p>`)
      .join('');
    return `
      <li class="ad-theme" data-theme-id="${escapeAttr(t.theme_id)}" data-theme-kind="${kind}" data-fragment-id="${escapeAttr(fid)}">
        <h3 class="ad-theme-title">
          <span>${escapeHtml(label)}</span>
          <span class="ad-theme-kind">${kind}</span>
        </h3>
        ${work ? `<p class="ad-theme-ref">${this.t('work.summary')}: <a href="#work-${escapeAttr(work.id)}">${escapeHtml(work.title)}</a></p>` : ''}
        <div class="ad-prose ad-theme-explanation">${body}</div>
        ${passage ? `
          <blockquote class="ad-theme-passage" data-passage-id="${escapeAttr(passage.id)}">
            ${preserveLinebreaks(passage.text)}
            <footer>— ${escapeHtml(passage.reference)}</footer>
          </blockquote>
        ` : ''}
      </li>
    `;
  }

  // ─────────────── RELATIONS ───────────────

  private renderRelations(a: Author): string {
    const allEmpty = RELATION_KEYS.every(k => a.relations[k].length === 0);
    if (allEmpty) return '';
    return `
      <details class="ad-section ad-section-relations ad-section-fold" id="section-relations" open>
        <summary class="ad-section-title">${this.t('section.relations')}</summary>
        <dl class="ad-relations" data-fragment-id="${escapeAttr(fragmentId(a.id, 'relations'))}">
          ${RELATION_KEYS.map(k => {
            const items = a.relations[k];
            if (items.length === 0) return '';
            return `
              <div class="ad-relation-row" data-relation="${k}" data-fragment-id="${escapeAttr(fragmentId(a.id, 'relations', k))}">
                <dt>${this.t(`relations.${k}`)}</dt>
                <dd>
                  ${items.map(id => {
                    const target = getDataService().getAuthor(id);
                    if (target) {
                      return `<button type="button" class="ad-chip ad-chip-link" data-author-link="${escapeAttr(id)}">${escapeHtml(target.name)}</button>`;
                    }
                    // Author not yet in the catalog — show id as plain chip
                    return `<span class="ad-chip ad-chip-pending" title="${escapeAttr(id)}">${escapeHtml(prettyId(id))}</span>`;
                  }).join('')}
                </dd>
              </div>
            `;
          }).join('')}
        </dl>
      </details>
    `;
  }

  private t(key: string): string {
    return this.context.i18n.t(key);
  }
}

// ─────────────── helpers ───────────────

function isLimMode(): boolean {
  return document.documentElement.dataset.display === 'lim';
}

/** Split HTML into paragraph inner-HTML strings (`<p>X</p><p>Y</p>` → ['X', 'Y']).
 *  Senza tag <p>, spezza sulle RIGHE VUOTE (criteri contenuti v2, doc 04):
 *  i campi generati separano i paragrafi con `\n\n` → ogni paragrafo è un
 *  frammento catturabile a sé. */
function splitParagraphs(html: string): string[] {
  const matches = html.match(/<p[^>]*>[\s\S]*?<\/p>/g);
  if (!matches) {
    const blocks = html.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
    return blocks.length > 1 ? blocks : [html];
  }
  return matches.map(m => m.replace(/^<p[^>]*>/, '').replace(/<\/p>$/, ''));
}

function formatDates(birth: number | null | undefined, death: number | null | undefined): string {
  if (!birth && !death) return '';
  const b = birth ?? '?';
  const d = death ?? '?';
  return `${b}–${d}`;
}

function initials(name: string): string {
  return name.split(/\s+/).map(s => s[0] ?? '').join('').slice(0, 2).toUpperCase();
}

/** Chip tassonomia. Se `scrollTheme` è valorizzato (esiste il trattamento del
 *  tema in pagina) diventa un BOTTONE-ancora che scrolla a quella sezione. */
function chip(kind: 'period' | 'movement' | 'genre', label: string, scrollTheme?: string): string {
  if (scrollTheme) {
    return `<button type="button" class="ad-chip ad-chip-${kind} ad-chip-link" data-scroll-theme="${escapeAttr(scrollTheme)}" title="${escapeHtml(label)} ↓">${escapeHtml(label)}</button>`;
  }
  return `<span class="ad-chip ad-chip-${kind}">${escapeHtml(label)}</span>`;
}

function prettyId(id: string): string {
  // 'william-shakespeare' → 'William Shakespeare'
  return id.split('-').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}

/** Preserve newlines inside <blockquote> (passages often use line breaks). */
function preserveLinebreaks(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

